import { buildJobDetailPath } from '@/features/jobs/utils/jobPaths';
import { buildStageDetailPath } from '@/features/pipelines/utils/stagePaths';
import type { GlobalSearchResult as GlobalSearchEntityResult } from '@/features/search/data';

type DirectRouteResultType = Exclude<GlobalSearchEntityResult['type'], 'job' | 'stage'>;

const directRoutePrefixes: Readonly<Record<DirectRouteResultType, string>> = {
  coverageDirectory: '/coverage-directories',
  coverageFile: '/coverage-files',
  coverageRun: '/coverage-runs',
  executionRun: '/execution-runs',
  feature: '/features',
  pipeline: '/pipelines',
  project: '/projects',
  release: '/releases',
  resultsFile: '/results-files',
  suite: '/suites',
  test: '/tests',
};

export type GlobalSearchNavigationResult = {
  id: string;
  source: 'navigation';
  type: 'navigation';
  title: string;
  subtitle: string | null;
  href: string;
  context: {
    organisationId: null;
    projectId: null;
  };
};

export type GlobalSearchEntityViewResult = Omit<GlobalSearchEntityResult, 'routeId'> & {
  source: 'entity';
  href: string;
};

export type GlobalSearchViewResult = GlobalSearchNavigationResult | GlobalSearchEntityViewResult;

export const buildGlobalSearchEntityHref = (result: GlobalSearchEntityResult): string => {
  switch (result.type) {
    case 'job':
      return buildJobDetailPath(result.routeId);
    case 'stage':
      return buildStageDetailPath(result.routeId);
    case 'coverageDirectory':
    case 'coverageFile':
    case 'coverageRun':
    case 'executionRun':
    case 'feature':
    case 'pipeline':
    case 'project':
    case 'release':
    case 'resultsFile':
    case 'suite':
    case 'test':
      return `${directRoutePrefixes[result.type]}/${encodeURIComponent(result.routeId)}`;
  }
};

export const toGlobalSearchEntityViewResult = (
  result: GlobalSearchEntityResult,
): GlobalSearchEntityViewResult => ({
  id: result.id,
  source: 'entity',
  type: result.type,
  title: result.title,
  subtitle: result.subtitle,
  href: buildGlobalSearchEntityHref(result),
  context: result.context,
});
